import { getAuthToken } from '../../services/auth/tokenStorage';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  Image,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { BottomNavBar } from '../../components/BottomNavBar';
import { useNavigation, useFocusEffect, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../../config';
import { ChecklistCardScroll } from '../../components/ChecklistCardScroll';
import {
  HEALTHCARE_DIRECTIVES_TASKS_CONFIG,
  ESTATE_PLANNING_TASKS_CONFIG,
  CARE_PREFERENCES_TASKS_CONFIG,
  PERSONAL_TASKS_CONFIG,
  MEDICAL_TASKS_CONFIG,
  PASSWORDS_TASKS_CONFIG,
  CONTACTS_TASKS_CONFIG,
  EXPENSES_TASKS_CONFIG,
  FINANCE_TASKS_CONFIG,
  PROPERTY_TASKS_CONFIG,
  FINAL_WISHES_TASKS_CONFIG,
  LOYALTY_POINTS_TASKS_CONFIG
} from '../../constants/taskIds';
import { TaskItem } from '../../components/TaskItem';
import { RootStackParamList } from '../../types/navigation';
import { vaultService } from '../../services/vaultService';
import { contactService } from '../../services/contactService';
import { expenseService } from '../../services/expenseService';
import { loyaltyPointService } from '../../services/loyaltyPointService';
import { documentStorageService } from '../../services/documentStorageService';
import { useUserSelection } from '../../contexts/UserSelectionContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { guardVaultEdit } from '../../utils/vaultEditGate';
import { UpgradeBanner } from '../../components/UpgradeBanner';

import { createLogger } from '../../utils/logger';
const logger = createLogger('HealthcareDirectivesChecklist');

export const HealthcareDirectivesChecklistScreen: React.FC = () => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute();
  const { selectedUserId, isViewingOthersData, availablePrimaryUsers } = useUserSelection();
  const { canViewOwnData, canEditData } = useSubscription();

  const viewingUserName = isViewingOthersData
    ? availablePrimaryUsers.find(u => u.id === selectedUserId)?.name || 'User'
    : null;
  const [completedTasks, setCompletedTasks] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>('cloud');
  const [completedPasswordTasks, setCompletedPasswordTasks] = useState<string[]>([]);
  const [completedContactTasks, setCompletedContactTasks] = useState<string[]>([]);
  const [completedExpenseTasks, setCompletedExpenseTasks] = useState<string[]>([]);
  const [completedLoyaltyPointTasks, setCompletedLoyaltyPointTasks] = useState<string[]>([]);
  const [completedDocumentTasks, setCompletedDocumentTasks] = useState<string[]>([]);
  const cacheRef = useRef<{ completed: number[]; timestamp: number } | null>(null);
  const CACHE_DURATION = 60000;

  const fetchCompletedTasks = async (useCache = true) => {
    const now = Date.now();
    if (useCache && cacheRef.current && (now - cacheRef.current.timestamp < CACHE_DURATION)) {
      setCompletedTasks(cacheRef.current.completed);
      setLoading(false);
      return;
    }
    setLoading(true);

    try {
      const token = await getAuthToken();
      if (!token) {
        setLoading(false);
        return;
      }

      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Completed tasks fetch timeout')), 3000);
      });

      const url = isViewingOthersData && selectedUserId
        ? `${API_URL}/api/tasks/user/completed-tasks?targetUserId=${selectedUserId}`
        : `${API_URL}/api/tasks/user/completed-tasks`;

      const fetchPromise = fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const completedRes = await Promise.race([fetchPromise, timeoutPromise]);
      let completed: number[] = [];
      if (completedRes.ok) {
        const completedData = await completedRes.json();
        logger.debug('API /user/completed-tasks response:', completedData);
        completed = (completedData.completedTaskIds || [])
          .map((id: any) => Number(id))
          .filter((id: number) => !isNaN(id));
      }

      // Include locally-declined tasks
      try {
        const declinedAdvanceDirective = await AsyncStorage.getItem('task_declined_56');
        if (declinedAdvanceDirective === 'true' && !completed.includes(56)) {
          completed.push(56);
        }
        const declinedMedicalPOA = await AsyncStorage.getItem('task_declined_61');
        if (declinedMedicalPOA === 'true' && !completed.includes(61)) {
          completed.push(61);
        }
        const declinedDNR = await AsyncStorage.getItem('task_declined_58');
        if (declinedDNR === 'true' && !completed.includes(58)) {
          completed.push(58);
        }
        const declinedHIPAA = await AsyncStorage.getItem('task_declined_500');
        if (declinedHIPAA === 'true' && !completed.includes(500)) {
          completed.push(500);
        }
      } catch {}

      setCompletedTasks(completed);
      cacheRef.current = { completed, timestamp: now };
    } catch (error: any) {
      if (error.message?.includes('Network request failed') ||
          error.message?.includes('fetch timeout') ||
          error.message?.includes('Request failed')) {
        logger.debug('Backend API not available, using empty completed tasks');
        setCompletedTasks([]);
      } else {
        logger.error('Error fetching completed tasks:', error);
        setCompletedTasks([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshTasks = () => {
    cacheRef.current = null;
    fetchCompletedTasks(false);
  };

  const fetchPasswordCompletion = async () => {
    try {
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Password fetch timeout')), 3000);
      });
      const fetchPromise = vaultService.getAllPasswords();
      const allPasswords = await Promise.race([fetchPromise, timeoutPromise]);
      const completedPasswordTasks: string[] = [];
      PASSWORDS_TASKS_CONFIG.forEach(task => {
        if (task.category) {
          const categoryPasswords = allPasswords.filter(p => p.category.toLowerCase() === task.category.toLowerCase());
          if (categoryPasswords.length > 0) {
            completedPasswordTasks.push(task.id.toString());
          }
        }
      });
      setCompletedPasswordTasks(completedPasswordTasks);
    } catch (error: any) {
      logger.debug('Vault API not available - using fallback');
      setCompletedPasswordTasks([]);
    }
  };

  const fetchContactCompletion = async () => {
    try {
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Contact fetch timeout')), 3000);
      });
      const fetchPromise = contactService.getAllContacts();
      const allContacts = await Promise.race([fetchPromise, timeoutPromise]);
      const completedContactTasks: string[] = [];
      CONTACTS_TASKS_CONFIG.forEach(task => {
        if (task.category) {
          const categoryContacts = allContacts.filter(c => c.category.toLowerCase() === task.category.toLowerCase());
          if (categoryContacts.length > 0) {
            completedContactTasks.push(task.id.toString());
          }
        }
      });
      setCompletedContactTasks(completedContactTasks);
    } catch (error: any) {
      logger.debug('Contact API not available - using fallback');
      setCompletedContactTasks([]);
    }
  };

  const fetchExpenseCompletion = async () => {
    try {
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Expense fetch timeout')), 3000);
      });
      const fetchPromise = expenseService.getAllExpenses();
      const allExpenses = await Promise.race([fetchPromise, timeoutPromise]);
      const completedExpenseTasks: string[] = [];
      EXPENSES_TASKS_CONFIG.forEach(task => {
        if (task.category) {
          const categoryExpenses = allExpenses.filter(e => e.category.toLowerCase() === task.category.toLowerCase());
          if (categoryExpenses.length > 0) {
            completedExpenseTasks.push(task.id.toString());
          }
        }
      });
      setCompletedExpenseTasks(completedExpenseTasks);
    } catch (error: any) {
      logger.debug('Expense API not available - using fallback');
      setCompletedExpenseTasks([]);
    }
  };

  const fetchLoyaltyPointCompletion = async () => {
    try {
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Loyalty point fetch timeout')), 3000);
      });
      const fetchPromise = loyaltyPointService.getAllLoyaltyPoints();
      const allLoyaltyPoints = await Promise.race([fetchPromise, timeoutPromise]);
      const completedLoyaltyPointTasks: string[] = [];
      LOYALTY_POINTS_TASKS_CONFIG.forEach(task => {
        if (task.category) {
          const categoryLoyaltyPoints = allLoyaltyPoints.filter(lp => lp.category.toLowerCase() === task.category.toLowerCase());
          if (categoryLoyaltyPoints.length > 0) {
            completedLoyaltyPointTasks.push(task.id.toString());
          }
        }
      });
      setCompletedLoyaltyPointTasks(completedLoyaltyPointTasks);
    } catch (error: any) {
      logger.debug('Loyalty Point API not available - using fallback');
      setCompletedLoyaltyPointTasks([]);
    }
  };

  const fetchDocumentCompletion = async () => {
    try {
      const allDocuments = await documentStorageService.listDocuments();
      const completedDocumentTasksList: string[] = [];
      const DOCUMENT_CATEGORIES = ['personal', 'legal', 'medical', 'financial', 'property', 'insurance', 'digital', 'other'];
      const DOCUMENT_TASK_IDS = ['dm1', 'dm2', 'dm3', 'dm4', 'dm5', 'dm6', 'dm7', 'dm8'];
      DOCUMENT_CATEGORIES.forEach((category, index) => {
        const categoryDocuments = allDocuments.filter(doc => doc.category?.toLowerCase() === category);
        if (categoryDocuments.length > 0) {
          completedDocumentTasksList.push(DOCUMENT_TASK_IDS[index]);
        }
      });
      setCompletedDocumentTasks(completedDocumentTasksList);
    } catch (error) {
      logger.error('Error fetching completed document tasks:', error);
      setCompletedDocumentTasks([]);
    }
  };

  useFocusEffect(
    useCallback(() => {
      refreshTasks();
      fetchPasswordCompletion();
      fetchContactCompletion();
      fetchExpenseCompletion();
      fetchLoyaltyPointCompletion();
      fetchDocumentCompletion();
      return () => {};
    }, [])
  );

  useEffect(() => {
    const refreshKey = (route as any)?.params?.refreshKey;
    if (refreshKey) {
      refreshTasks();
    }
  }, [route]);

  useEffect(() => {
    if (selectedUserId) {
      logger.debug('HealthcareDirectivesChecklistScreen: selectedUserId changed, refetching...', selectedUserId);
      cacheRef.current = null;
      fetchCompletedTasks(false);
    }
  }, [selectedUserId]);

  const renderTaskItem = (task: any) => {
    const taskId = Number(task.id);
    const isCompleted = completedTasks.includes(taskId);
    const isTaskDisabled = !canViewOwnData && !isViewingOthersData;
    let showButton = true;
    let buttonText = '';
    let onPress: (() => void) = () => {};
    let buttonColor = '#29BFC0';

    if (subscriptionStatus === 'free') {
      showButton = false;
    } else {
      showButton = true;

      if (isViewingOthersData) {
        if (isCompleted) {
          buttonText = 'View Data';
          buttonColor = '#FF9F5A';
          onPress = () => (navigation as any).navigate('HealthcareDirectives', {
            screen: task.dataScreen as any,
            params: { targetUserId: selectedUserId, viewOnly: true }
          });
        } else {
          buttonText = 'Not Completed';
          buttonColor = '#CCCCCC';
          onPress = () => {};
        }
      } else if (isCompleted) {
        buttonText = 'Review Information';
        buttonColor = '#FF9F5A';
        onPress = () => (navigation as any).navigate('HealthcareDirectives', { screen: task.dataScreen as any });
      } else {
        buttonText = 'Click to Complete';
        buttonColor = '#29BFC0';
        onPress = () =>
          guardVaultEdit(canEditData, navigation, () => (navigation as any).navigate('HealthcareDirectives', { screen: task.inputScreen as any }));
      }
    }
    return (
      <TaskItem
        key={task.id}
        task={{ id: String(task.id), title: task.title }}
        isCompleted={isCompleted}
        onPress={onPress}
        hideActionButton={!showButton}
        buttonText={buttonText}
        buttonColor={buttonColor}
        subscriptionTier={subscriptionStatus}
        onCheckboxPress={() => {}}
        isDisabled={isTaskDisabled}
      />
    );
  };

  const mappedCategories = [
    { id: 'all', name: 'All Tasks', icon: require('../../assets/icons/all-tasks.png') },
    { id: 'personal', name: 'Personal', icon: require('../../assets/icons/personal.png') },
    { id: 'medical', name: 'Medical', icon: require('../../assets/icons/medical.png') },
    { id: 'finance', name: 'Finance', icon: require('../../assets/icons/financial.png') },
    { id: 'property', name: 'Property', icon: require('../../assets/icons/property.png') },
    { id: 'finalWishes', name: 'Final Wishes', icon: require('../../assets/icons/final-wishes.png') },
    { id: 'healthcareDirectives', name: 'Healthcare Directives', icon: require('../../assets/icons/healthcare.png') },
    { id: 'estatePlanning', name: 'Estate Planning', icon: require('../../assets/icons/estate-planning.png') },
    { id: 'carePreferences', name: 'Care Preferences', icon: require('../../assets/icons/final-care.png') },
    { id: 'passwords', name: 'Passwords', icon: require('../../assets/icons/password.png') },
    { id: 'contacts', name: 'Contacts', icon: require('../../assets/icons/all-contacts.png') },
    { id: 'expenses', name: 'Expenses', icon: require('../../assets/icons/expenses.png') },
    { id: 'documents', name: 'Documents', icon: require('../../assets/icons/folder.png') },
    { id: 'loyaltyPoints', name: 'Loyalty Points', icon: require('../../assets/icons/loyalty.png') },
  ].map(cat => {
    if (cat.id === 'healthcareDirectives') {
      const healthcareDirectivesIds = new Set(HEALTHCARE_DIRECTIVES_TASKS_CONFIG.map(t => Number(t.id)));
      const completedCount = completedTasks.filter(id => healthcareDirectivesIds.has(Number(id))).length;
      return { ...cat, count: completedCount, totalTasks: HEALTHCARE_DIRECTIVES_TASKS_CONFIG.length };
    } else if (cat.id === 'estatePlanning') {
      const estatePlanningIds = new Set(ESTATE_PLANNING_TASKS_CONFIG.map(t => Number(t.id)));
      const completedCount = completedTasks.filter(id => estatePlanningIds.has(Number(id))).length;
      return { ...cat, count: completedCount, totalTasks: ESTATE_PLANNING_TASKS_CONFIG.length };
    } else if (cat.id === 'carePreferences') {
      const carePreferencesIds = new Set(CARE_PREFERENCES_TASKS_CONFIG.map(t => Number(t.id)));
      const completedCount = completedTasks.filter(id => carePreferencesIds.has(Number(id))).length;
      return { ...cat, count: completedCount, totalTasks: CARE_PREFERENCES_TASKS_CONFIG.length };
    } else if (cat.id === 'personal') {
      const personalIds = new Set(PERSONAL_TASKS_CONFIG.map(t => Number(t.id)));
      const completedCount = completedTasks.filter(id => personalIds.has(Number(id))).length;
      return { ...cat, count: completedCount, totalTasks: PERSONAL_TASKS_CONFIG.length };
    } else if (cat.id === 'medical') {
      const medicalIds = new Set(MEDICAL_TASKS_CONFIG.map(t => Number(t.id)));
      const completedCount = completedTasks.filter(id => medicalIds.has(Number(id))).length;
      return { ...cat, count: completedCount, totalTasks: MEDICAL_TASKS_CONFIG.length };
    } else if (cat.id === 'finance') {
      const financeIds = new Set(FINANCE_TASKS_CONFIG.map(t => Number(t.id)));
      const completedCount = completedTasks.filter(id => financeIds.has(Number(id))).length;
      return { ...cat, count: completedCount, totalTasks: FINANCE_TASKS_CONFIG.length };
    } else if (cat.id === 'property') {
      const propertyIds = new Set(PROPERTY_TASKS_CONFIG.map(t => Number(t.id)));
      const completedCount = completedTasks.filter(id => propertyIds.has(Number(id))).length;
      return { ...cat, count: completedCount, totalTasks: PROPERTY_TASKS_CONFIG.length };
    } else if (cat.id === 'finalWishes') {
      const finalWishesIds = new Set(FINAL_WISHES_TASKS_CONFIG.map(t => Number(t.id)));
      const completedCount = completedTasks.filter(id => finalWishesIds.has(Number(id))).length;
      return { ...cat, count: completedCount, totalTasks: FINAL_WISHES_TASKS_CONFIG.length };
    } else if (cat.id === 'passwords') {
      return { ...cat, count: completedPasswordTasks.length, totalTasks: PASSWORDS_TASKS_CONFIG.length };
    } else if (cat.id === 'contacts') {
      return { ...cat, count: completedContactTasks.length, totalTasks: CONTACTS_TASKS_CONFIG.length };
    } else if (cat.id === 'expenses') {
      return { ...cat, count: completedExpenseTasks.length, totalTasks: EXPENSES_TASKS_CONFIG.length };
    } else if (cat.id === 'loyaltyPoints') {
      return { ...cat, count: completedLoyaltyPointTasks.length, totalTasks: LOYALTY_POINTS_TASKS_CONFIG.length };
    } else if (cat.id === 'documents') {
      return { ...cat, count: completedDocumentTasks.length, totalTasks: 8 };
    } else {
      return { ...cat, count: 0, totalTasks: 0 };
    }
  });

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#29BFC0" />
          <Text style={styles.loadingText}>Loading checklist...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.mainContent}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <View style={styles.headerPlaceholder} />
        </View>

        {isViewingOthersData && viewingUserName && (
          <View style={styles.viewingBanner}>
            <Text style={styles.viewingBannerText}>
              Viewing {viewingUserName}'s Data (View Only)
            </Text>
          </View>
        )}

        {!isViewingOthersData && <UpgradeBanner variant="compact" />}

        <ChecklistCardScroll
          categories={mappedCategories}
          selectedCategory="healthcareDirectives"
          navigation={navigation}
          onCategoryPress={() => {}}
        />
        <View style={styles.titleContainer}>
          <Text style={styles.title}>Healthcare Directives</Text>
        </View>
        <ScrollView
          style={styles.checklistContainer}
          contentContainerStyle={styles.checklistContentContainer}
          showsVerticalScrollIndicator={false}
        >
          {HEALTHCARE_DIRECTIVES_TASKS_CONFIG.map(renderTaskItem)}
        </ScrollView>
      </View>
      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    paddingTop: ms(25),
  },
  mainContent: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: '10%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(30),
    height: s(30),
    tintColor: '#333',
  },
  logo: {
    width: ms(125, 0.3),
    height: ms(125, 0.3),
  },
  headerPlaceholder: {
    width: s(30),
    height: s(30),
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: ms(10),
    marginHorizontal: ms(15),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
  },
  checklistContainer: {
    flex: 1,
    paddingHorizontal: ms(15),
  },
  checklistContentContainer: {
    paddingTop: ms(10),
    paddingBottom: ms(150),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: ms(10),
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  viewingBanner: {
    backgroundColor: '#29BFC0',
    paddingVertical: ms(10),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(15),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewingBannerText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});
